import { GetStaticPaths, GetStaticProps } from 'next';

import { getServerPageGetProjectDetail, getServerPageGetProjectSlugs } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import {
	BodyTextFieldsFragment,
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	PageInformationFieldsFragment,
	ProjectDetailSingleFieldsFragment,
	ProjectFieldsFragment
} from 'api/graphql-types';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import IntroHeader from 'modules/intro-header/intro-header';
import BodyText from 'modules/body-text/normal/normal-body-text';
import { BaseModule } from 'modules/base-module/base-module';
import { Head } from 'modules/head/head';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import { ROUTES } from 'constants/routes';
import { buildSlugPaths } from 'utils/build-slug-paths';

interface IProjectDetailPageProps extends IBasePageProps {
	pageInformation?: PageInformationFieldsFragment;
	projectDetail?: ProjectDetailSingleFieldsFragment;
	projectBySlug?: ProjectFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout?: any;
}

const ProjectDetailPage = (props: IProjectDetailPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.projectBySlug || null,
		pageQuery: () => getServerPageGetProjectDetail({ variables: props.variables }, client)
	});

	// Dirty fix for quick polish page
	if (page?.slug === '/projecten/life-co-2-sand-polski' && typeof document !== 'undefined') {
		document.documentElement.setAttribute('lang', 'pl');

		const selected = document.querySelector('#project') as HTMLElement | null;

		if (selected !== null) {
			selected.style.fontFamily = 'Fedra Serif LCG';
		}
	}

	return (
		<Layout
			contentId={props?.id}
			typeName={props?.projectBySlug?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={page?.seo} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'Project' ? (
				<div id="project">
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: page?.header
								?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
							padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
						}}
					>
						<IntroHeader
							module={{
								title: page?.header?.title || '',
								backgroundColor: page?.header?.backgroundColor,
								excerpt: page?.header?.excerpt || '',
								cover: page?.header?.cover
							}}
							slug={page.slug}
						/>
					</BaseModule>
					<BaseModule defaultModuleOptions={page?.bodyText?.defaultModuleOptions}>
						<BodyText module={page?.bodyText as BodyTextFieldsFragment} updatedAt={page?.updatedAt} />
					</BaseModule>
					<ModuleBuilder modules={page?.modules || []} slug={page.slug} />
					<ModuleBuilder modules={props.projectDetail?.modules || []} slug={page.slug} />
				</div>
			) : props.isPasswordProtected ? (
				<PasswordLayout
					locale={props.variables?.locale}
					slug={props.variables?.slug}
					pageData={props.passwordPageLayout}
				>
					<PasswordModule />
				</PasswordLayout>
			) : null}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const result = await getServerPageGetProjectSlugs({}, client);
	const slugs = result.props.data.findAllSlugs;

	return {
		paths: buildSlugPaths(slugs, ROUTES.PROJECTS.slug, ctx.defaultLocale),
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug ? `/${ROUTES.PROJECTS.slug}/${ctx.params.slug}` : '/';
		const variables = { slug, locale, publicationState };

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug: slug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::project.project',
			contentTypeName: 'project',
			fetchPage: () => getServerPageGetProjectDetail({ variables }, client),
			extractInformation: (data) => data?.props?.data?.pageInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.projectBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			}
		});

		let projectDetail = null;
		if (!passwordResult.isPasswordProtected && passwordResult.page) {
			const detail = await getServerPageGetProjectDetail({ variables }, client);
			projectDetail = detail.props.data?.projectDetail?.data?.attributes || null;
		}

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			return { notFound: true };
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				variables,
				pageInformation: passwordResult.pageInformation,
				projectDetail: projectDetail,
				projectBySlug: passwordResult.page,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in project detail', error);
		return { notFound: true };
	}
};

export default ProjectDetailPage;
